import React, { useState } from 'react';
import { MapPin, Navigation, Clock, Users, Car, Sparkles, Zap, Shield, Star, Cpu } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const RideOptionCard = ({ icon: Icon, title, description, price, time, features }) => (
  <div className="glow-card bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-border-light transition-all duration-400 cursor-pointer group">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center">
        <div className="w-12 h-12 bg-dark-surface border border-dark-border rounded-lg flex items-center justify-center mr-4 group-hover:bg-accent/10 group-hover:border-accent/20 transition-colors">
          <Icon className="h-6 w-6 text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white group-hover:text-accent transition-colors">{title}</h3>
          {time && <div className="flex items-center text-sm text-gray-500 mt-1"><Clock className="h-3 w-3 mr-1" /><span>{time}</span></div>}
        </div>
      </div>
      {price && <div className="text-right"><div className="text-xl font-bold text-white">{price}</div></div>}
    </div>
    {description && <p className="text-sm text-gray-400 mb-3">{description}</p>}
    {features && <div className="flex flex-wrap gap-2">{features.map((feature, index) => (<span key={index} className="px-2 py-1 bg-dark-surface text-gray-400 text-xs rounded-lg border border-dark-border">{feature}</span>))}</div>}
  </div>
);

const Ride = () => {
  const { t, language } = useLanguage();
  usePageTitle('Ride Help');
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const minutesSuffix = language === 'zh' ? '分钟' : ' min';
  const rideOptions = [
    { icon: Cpu, titleKey: 'autoDrive', descKey: 'autoDriveDesc', price: '$15-20', timeKey: 'comingSoon', features: ['featureAuto', 'featureNav', 'featureSafe', 'featureFuture'] },
    { icon: Car, titleKey: 'aiRide', descKey: 'aiRideDesc', price: '$12-18', time: '3-5', features: ['featureRoute', 'featureMatch', 'featureTrack'] },
    { icon: Car, titleKey: 'economy', descKey: 'economyDesc', price: '$8-12', time: '5-8', features: ['featureBudget', 'featureFast'] },
    { icon: Car, titleKey: 'comfort', descKey: 'comfortDesc', price: '$15-22', time: '4-6', features: ['featureSeat', 'featureSpace'] },
    { icon: Car, titleKey: 'luxury', descKey: 'luxuryDesc', price: '$25-35', time: '2-4', features: ['featureCar', 'featureDriver'] },
  ].map((opt) => ({
    icon: opt.icon,
    title: t(`ride.${opt.titleKey}`),
    description: t(`ride.${opt.descKey}`),
    price: opt.price,
    time: opt.timeKey ? t(`ride.${opt.timeKey}`) : opt.time + minutesSuffix,
    features: (opt.features || []).map((k) => t(`ride.${k}`)),
  }));

  return (
    <div className="-mt-16">
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/12 via-indigo-600/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-indigo-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold uppercase text-accent bg-accent/10 border border-accent/20 rounded-full">
            {t('ride.inDevelopment')}
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            {t('ride.title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl">
            {t('ride.subtitle')}
          </p>
          <p className="mt-2 text-base text-gray-500 max-w-2xl">
            {t('ride.description')}
          </p>
        </div>
      </section>

      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-gradient bg-dark-card rounded-2xl shadow-dark-card p-6 md:p-8 max-w-3xl mx-auto">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('ride.pickupLabel')}</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input type="text" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder={t('ride.pickupPlaceholder')} className="w-full pl-10 pr-4 py-3 bg-dark-surface border border-dark-border text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('ride.destinationLabel')}</label>
                <div className="relative">
                  <Navigation className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={t('ride.destinationPlaceholder')} className="w-full pl-10 pr-4 py-3 bg-dark-surface border border-dark-border text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('ride.departureTimeLabel')}</label>
                  <select className="w-full px-4 py-3 bg-dark-surface border border-dark-border text-white rounded-xl focus:ring-2 focus:ring-accent/30 outline-none">{[t('ride.departureNow'), t('ride.departureLater'), t('ride.departureTomorrow')].map((opt, i) => <option key={i}>{opt}</option>)}</select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('ride.passengersLabel')}</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <select className="w-full pl-10 pr-4 py-3 bg-dark-surface border border-dark-border text-white rounded-xl focus:ring-2 focus:ring-accent/30 outline-none">
                      <option>{t('ride.passengersCount', { n: 1 })}</option>
                      <option>{t('ride.passengersCount', { n: 2 })}</option>
                      <option>{t('ride.passengersCount', { n: 3 })}</option>
                      <option>{t('ride.passengersCount', { n: 4 })}</option>
                      <option>{t('ride.passengers5Plus')}</option>
                    </select>
                  </div>
                </div>
              </div>
              <button className="w-full bg-white text-dark py-4 rounded-full font-bold text-lg hover:shadow-glow-white transition-all duration-400 flex items-center justify-center">
                <Zap className="h-5 w-5 mr-2" />AI智能匹配叫车
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features — dark band */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/15 via-indigo-600/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-gradient-to-tr from-indigo-600/10 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 border border-white/10 rounded-full mb-4">
              <Sparkles className="h-8 w-8 text-accent" />
            </div>
            <h2 className="text-3xl font-bold mb-4 text-white">{t('ride.futureTechTitle')}</h2>
            <p className="text-xl text-gray-400">{t('ride.futureTechDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center glow-card hover:border-dark-border-light transition-all duration-400">
              <Zap className="h-8 w-8 mx-auto mb-3 text-accent" />
              <h3 className="font-bold mb-2 text-white">{t('ride.routeOptimization')}</h3>
              <p className="text-sm text-gray-400">{t('ride.routeOptimizationDesc')}</p>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center glow-card hover:border-dark-border-light transition-all duration-400">
              <Sparkles className="h-8 w-8 mx-auto mb-3 text-accent" />
              <h3 className="font-bold mb-2 text-white">{t('ride.smartDispatch')}</h3>
              <p className="text-sm text-gray-400">{t('ride.smartDispatchDesc')}</p>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center glow-card hover:border-dark-border-light transition-all duration-400">
              <Shield className="h-8 w-8 mx-auto mb-3 text-accent" />
              <h3 className="font-bold mb-2 text-white">{t('ride.safeSmart')}</h3>
              <p className="text-sm text-gray-400">{t('ride.safeSmartDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8">{t('ride.chooseVehicleTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rideOptions.map((option, index) => (
              <RideOptionCard key={index} {...option} />
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">{t('ride.whyChooseTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'AI智能匹配', desc: '最快3分钟到达，智能调度系统' },
              { icon: Shield, title: '安全可靠', desc: '实名认证司机，全程保险保障' },
              { icon: Star, title: '优质服务', desc: '专业司机，舒适车辆，贴心服务' },
              { icon: Clock, title: '24/7服务', desc: '全天候服务，随时为您出行' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-dark-card border border-dark-border rounded-full flex items-center justify-center mx-auto mb-4"><item.icon className="h-8 w-8 text-accent" /></div>
                <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future AI Vision — dark band */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/15 via-indigo-600/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-gradient-to-tr from-indigo-600/10 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-4">未来AI愿景</h2>
          <p className="text-center text-gray-400 mb-10 max-w-3xl mx-auto">
            {t('ride.devNote')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            <div className="bg-dark-surface border border-white/10 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Cpu className="h-6 w-6 text-accent mr-3" />
                <h3 className="font-bold text-white">自动驾驶</h3>
                <span className="ml-2 px-2 py-1 bg-accent/10 text-accent text-xs font-bold rounded">即将推出</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                完全自动驾驶车辆，无需司机，AI全程操控，让您享受未来出行的便利
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• 全自动导航和驾驶</li>
                <li>• AI实时路况分析</li>
                <li>• 高精度地图和传感器</li>
                <li>• 安全可靠的自动驾驶系统</li>
              </ul>
            </div>
            <div className="bg-dark-surface border border-white/10 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Sparkles className="h-6 w-6 text-accent mr-3" />
                <h3 className="font-bold text-white">预测性调度</h3>
              </div>
              <p className="text-sm text-gray-400">
                AI将预测您的出行需求，提前调度车辆到您附近，实现零等待叫车
              </p>
            </div>
            <div className="bg-dark-surface border border-white/10 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Zap className="h-6 w-6 text-accent mr-3" />
                <h3 className="font-bold text-white">个性化路线</h3>
              </div>
              <p className="text-sm text-gray-400">
                根据您的习惯和偏好，AI自动规划最适合的路线，避开拥堵
              </p>
            </div>
            <div className="bg-dark-surface border border-white/10 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Shield className="h-6 w-6 text-accent mr-3" />
                <h3 className="font-bold text-white">智能安全</h3>
              </div>
              <p className="text-sm text-gray-400">
                AI实时监控行程，异常情况自动预警，保障您的出行安全
              </p>
            </div>
            <div className="bg-dark-surface border border-white/10 rounded-xl p-6 md:col-span-2">
              <div className="flex items-center mb-3">
                <Users className="h-6 w-6 text-accent mr-3" />
                <h3 className="font-bold text-white">智能拼车</h3>
              </div>
              <p className="text-sm text-gray-400">
                AI智能匹配同路线的乘客，降低出行成本，环保出行
              </p>
            </div>
          </div>
          <div className="bg-dark-surface border border-white/10 rounded-xl p-6 text-center max-w-2xl mx-auto">
            <h3 className="text-xl font-bold mb-2 text-white">自动驾驶技术正在开发中</h3>
            <p className="text-gray-400 text-sm">
              {t('ride.futureNote')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ride;
